import AvatarComponent from 'components/avatar'
import { LineIcon, RouteIcon, ScheduleGroupIcon, SchoolIcon } from 'components/icons'
import { CarIcon } from 'components/icons/CarIcon'
import { UsersIcon } from 'components/icons/UsersIcon'
import ModalConfirmRemoveComponent from 'components/modal-confirm-remove'
import './index.css'

export interface CardUserStudentActionProps {
  label: string
  icon: string | JSX.Element
  onClick: string | (() => Promise<void>)
  modal?: {
    title: string
    body: string
    actionText: string
  }
}

const CardUserStudentComponent: React.FC<{
  userName: string
  userRelationship?: string
  avatarSize?: number
  photoLink?: string
  failedPhotoLinks: Set<string>
  onFailedPhotoLink: (url: string) => void
  distanceMiles?: string
  studentName?: string
  scheduleGroupName?: string
  drivesCompleted?: string
  ridesProvided?: string
  actions?: CardUserStudentActionProps[]
  onClick?: () => void
}> = ({
  userName,
  userRelationship,
  avatarSize = 12,
  photoLink,
  failedPhotoLinks,
  onFailedPhotoLink,
  distanceMiles,
  studentName,
  scheduleGroupName,
  drivesCompleted,
  ridesProvided,
  actions = [],
  onClick,
}) => {
  return (
    <div className={`flex flex-col items-start gap-3 self-stretch py-2 lg:px-2 lg:border lg:rounded-xl`}>
      <div className='flex justify-between items-center self-stretch gap-2'>
        <div
          className={`flex items-center gap-1 flex-1 ${onClick ? 'cursor-pointer transition-shadow rounded-lg bg-transparent hover:bg-gray-100' : ''}`}
          onClick={onClick}
        >
          <AvatarComponent
            size={avatarSize}
            name={userName}
            photoLink={photoLink}
            failedPhotoLinks={failedPhotoLinks}
            onFailedPhotoLink={onFailedPhotoLink}
          />
          <div className='flex flex-col'>
            <div className='flex items-center gap-1'>
              <span
                className={`max-w-[${userRelationship ? 20 : 40}vw] w-auto text-[#474747] text-sm font-medium truncate overflow-hidden`}
              >
                {userName}
              </span>
              {userRelationship && (
                <div className='flex items-center gap-1'>
                  {userName && <LineIcon />}
                  <span
                    className={`max-w-[${userName ? 20 : 40}vw] w-auto text-[#797979] text-xs font-medium truncate overflow-hidden`}
                  >
                    {userRelationship}
                  </span>
                </div>
              )}
            </div>
            <div className='flex items-center gap-1'>
              {studentName && (
                <div className='flex items-center gap-1'>
                  <SchoolIcon />
                  <span
                    className={`max-w-[${scheduleGroupName ? 20 : 40}vw] w-auto text-headings text-sm font-normal truncate overflow-hidden`}
                  >
                    {studentName}
                  </span>
                </div>
              )}
              {scheduleGroupName && (
                <div className='flex items-center gap-1'>
                  {studentName && <LineIcon />}
                  <ScheduleGroupIcon />
                  <span
                    className={`max-w-[${studentName ? 20 : 40}vw] w-auto text-headings text-sm font-normal truncate overflow-hidden`}
                  >
                    {scheduleGroupName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {actions.length > 0 && (
          <div className='flex items-end gap-0'>
            {actions.map(({ label, icon, onClick, modal }, index) =>
              modal && typeof onClick === 'function' ? (
                <ModalConfirmRemoveComponent
                  data_testid={`${label}_testid`}
                  key={index}
                  icon={icon as JSX.Element}
                  title={modal.title}
                  body={modal.body}
                  actionText={modal.actionText}
                  onClick={onClick}
                />
              ) : typeof onClick === 'string' ? (
                <a
                  href={onClick}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={label || 'Open link'}
                  className='flex h-10 w-10 items-center justify-center rounded-lg bg-transparent hover:bg-gray-100'
                >
                  <img src={icon as string} alt={label} className='w-10 h-10' />
                </a>
              ) : (
                // onClick is a function
                <button
                  key={index}
                  onClick={onClick}
                  data-testid='group-info-button-testid'
                  className='flex h-10 w-10 items-center justify-center rounded-lg bg-transparent p-0 hover:bg-gray-100'
                >
                  {icon}
                </button>
              ),
            )}
          </div>
        )}
      </div>

      {(distanceMiles || drivesCompleted || ridesProvided) && (
        <div className='flex items-center gap-2'>
          {distanceMiles && (
            <div className='flex items-center gap-1'>
              <RouteIcon />
              <span className='w-auto text-headings text-sm font-normal'>{distanceMiles} away</span>
            </div>
          )}
          {drivesCompleted && (
            <div className='flex items-center gap-1'>
              {distanceMiles && <LineIcon />}
              <CarIcon size={24} />
              <span className='w-auto text-headings text-sm font-normal'>{drivesCompleted}</span>
            </div>
          )}
          {ridesProvided && (
            <div className='flex items-center gap-1'>
              {(drivesCompleted || distanceMiles) && <LineIcon />}
              <UsersIcon size={24} />
              <span className='w-auto text-headings text-sm font-normal'>{ridesProvided}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CardUserStudentComponent
